import React, {useEffect, useState, useRef, useCallback, useMemo} from "react";
import ReactToPrint from "react-to-print";
import {useSelector} from "react-redux";
import {useLocation} from "react-router-dom";
import api from "api";
import {
  createFacilityMonitoring,
  deleteFacilityMonitoring,
  updateFacilityMonitoring,
} from "helpers/facilityMonitoring";
import {useToaster} from "context/TosterContext";
import {Button} from "ui/base/Button";
import {ModalWarning} from "ui/base/ModalWarning";
import {
  FormRow,
  FormCell,
  FormTable,
  FormHeader,
  FormInput,
  FormLabel,
  FormSelectLabel,
} from "./MonthlyVisualObservationStyled";

import {
  FacilityMonitoringAreaProps,
  FacilityMonitoringAreaRowsProps,
  initialFormData,
  InspectionFormData,
  StormWaterInspectionFormProps,
} from "../types";

interface RootState {
  user: {
    id: number;
  } | null;
}

interface RadioGroupProps {
  name: string;
  value?: boolean;
  onChange: ( value: boolean ) => void;
  disabled?: boolean;
  required?: boolean;
}

const RadioGroup: React.FC<RadioGroupProps> = ( { name, value, onChange, disabled = false, required = false } ) => (
  <div style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 15 }}>
    <FormSelectLabel>
      <input
        type="radio"
        name={name}
        value="true"
        checked={value === true}
        onChange={() => onChange(true)}
        disabled={disabled}
        required={required}
      />
      Yes
    </FormSelectLabel>
    <p>or</p>
    <FormSelectLabel>
      <input
        type="radio"
        name={name}
        value="false"
        checked={value === false}
        onChange={() => onChange(false)}
        disabled={disabled}
        required={required}
      />
      No
    </FormSelectLabel>
  </div>
);

interface ReadOnlyFieldProps {
  label: string;
  value: string | null | undefined;
}

const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ( { label, value } ) => (
  <FormCell style={{ display: "flex", flexDirection: "column", gap: 5 }}>
    <FormLabel>{label}</FormLabel>
    <span>{value || <em>N/A</em>}</span>
  </FormCell>
);

interface ExistingAreaRowProps {
  area: FacilityMonitoringAreaProps;
  isReadOnly: boolean;
  onChange: ( id: number, field: keyof FacilityMonitoringAreaRowsProps, value: string ) => void;
  onDelete: ( id: number ) => void;
}

const ExistingAreaRow: React.FC<ExistingAreaRowProps> = ( { area, isReadOnly, onChange, onDelete } ) => {
  const handleChange = ( field: keyof FacilityMonitoringAreaRowsProps, value: string ) => {
    onChange(area.id, field, value);
  };

  return isReadOnly ? (
    <FormRow>
      <FormCell>{area.area || <em>N/A</em>}</FormCell>
      <FormCell>{area.issue || <em>N/A</em>}</FormCell>
      <FormCell>{area.revision || <em>N/A</em>}</FormCell>
    </FormRow>
  ) : (
    <FormRow>
      <FormCell>
        <FormInput type="text" value={area.area || ""} onChange={( e ) => handleChange("area", e.target.value)}/>
      </FormCell>
      <FormCell>
        <FormInput type="text" value={area.issue || ""} onChange={( e ) => handleChange("issue", e.target.value)}/>
      </FormCell>
      <FormCell>
        <FormInput type="text" value={area.revision || ""}
                   onChange={( e ) => handleChange("revision", e.target.value)}/>
      </FormCell>
      <FormCell style={{ maxWidth: "100px" }}>
        <FormLabel
          onClick={() => onDelete(area.id)}
          style={{
            backgroundColor: "red",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "7px",
            color: "white",
            opacity: 0.9,
            cursor: "pointer",
          }}
        >
          Delete
        </FormLabel>
      </FormCell>
    </FormRow>
  );
};

interface NewAreaRowProps {
  row: FacilityMonitoringAreaRowsProps;
  index: number;
  isReadOnly: boolean;
  onChange: ( index: number, field: keyof FacilityMonitoringAreaRowsProps, value: string ) => void;
  onDelete: ( index: number ) => void;
  required: boolean | undefined;
}

const NewAreaRow: React.FC<NewAreaRowProps> = ( { row, index, isReadOnly, onChange, onDelete, required } ) => {
  const handleChange = ( field: keyof FacilityMonitoringAreaRowsProps, value: string ) => {
    onChange(index, field, value);
  };

  return (
    <FormRow key={`new-area-${index}`}>
      <FormCell>
        <FormInput
          type="text"
          value={row.area || ""}
          onChange={( e ) => handleChange("area", e.target.value)}
          disabled={isReadOnly}
          required={required}
        />
      </FormCell>
      <FormCell>
        <FormInput
          type="text"
          value={row.issue || ""}
          onChange={( e ) => handleChange("issue", e.target.value)}
          disabled={isReadOnly}
          required={required}
        />
      </FormCell>
      <FormCell>
        <FormInput
          type="text"
          value={row.revision || ""}
          onChange={( e ) => handleChange("revision", e.target.value)}
          disabled={isReadOnly}
          required={required}
        />
      </FormCell>
      {!isReadOnly && (
        <FormCell style={{ maxWidth: "100px" }}>
          <FormLabel
            onClick={() => onDelete(index)}
            style={{
              backgroundColor: "red",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "7px",
              color: "white",
              opacity: 0.9,
              cursor: "pointer",
            }}
          >
            Delete
          </FormLabel>
        </FormCell>
      )}
    </FormRow>
  );
};

const getInitialMonthIndex = ( monthNames: string[], search: string, formDataMonth?: string ): number => {
  if ( formDataMonth ) {
    const index = monthNames.findIndex(( m ) => m.toLowerCase() === formDataMonth.toLowerCase());
    if ( index !== -1 ) {
      return index;
    }
  }const params = new URLSearchParams(search);
  const urlMonth = params.get("month");
  if ( urlMonth ) {
    const index = monthNames.findIndex(( m ) => m.toLowerCase() === urlMonth.toLowerCase());
    if ( index !== -1 ) {
      return index;
    }
  }
  return 0;
};

const StormWaterInspectionForm: React.FC<StormWaterInspectionFormProps> = ( {
                                                                              formData,
                                                                              year,
                                                                              wdid,
                                                                              isReadOnly = false,
                                                                            } ) => {
  const { addToast } = useToaster();
  const componentRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();

  const user = useSelector(( state: RootState ) => state.user);

  const [inspectionData, setInspectionData] = useState<InspectionFormData>(initialFormData);
  const [existingAreas, setExistingAreas] = useState<FacilityMonitoringAreaProps[]>([]);
  const [newAreas, setNewAreas] = useState<FacilityMonitoringAreaRowsProps[]>([
    { id: undefined, area: "", issue: "", revision: "" },
  ]);

  const monthNames = useMemo(() => [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ], [])  ;

  const [selectedMonthIndex, setSelectedMonthIndex] = useState<number>(() =>
    getInitialMonthIndex(monthNames, location.search, formData?.month)
  );

  const currentMonth = monthNames[selectedMonthIndex];

  useEffect(() => {
    // Only set inspectionData on initial load, don't overwrite user input
    if (formData && !inspectionData.id && inspectionData.title === initialFormData.title && inspectionData.date_time === initialFormData.date_time) {
      setInspectionData(formData);
    } else if (!formData && !inspectionData.id && inspectionData.title === initialFormData.title && inspectionData.date_time === initialFormData.date_time) {
      setInspectionData(initialFormData);
    }
    
    if ( formData?.month && !inspectionData.month && formData.id ) {
      const monthIndex = monthNames.findIndex(( m ) => m.toLowerCase() === formData.month.toLowerCase());
      if ( monthIndex !== -1 ) {
        setSelectedMonthIndex(monthIndex);
      }
    }
  }, [formData]);

  useEffect(() => {
    // Only set originalAreas if it's not already set
    if (formData?.id && !inspectionData.originalAreas) {
      setInspectionData(( prev ) => ({
        ...prev,
        originalAreas: []
      }));
    } else if (!formData?.id && !inspectionData.originalAreas) {
      setInspectionData(( prev ) => ({
        ...prev,
        originalAreas: []
      }));
    }
  }, [formData?.id]);

  const fetchExistingAreas = async () => {
    if ( inspectionData.id ) {
      try {
        const res = await api.get(`facility_monitoring_area?facility_monitoring_id=eq.${inspectionData.id}`);
        if ( Array.isArray(res.data) && res.data.length > 0 ) {
          setExistingAreas(res.data);
          setInspectionData(( prev ) => ({
            ...prev,
            originalAreas: res.data
          }));
        } else {
          setInspectionData(( prev ) => ({
            ...prev,
            originalAreas: []
          }));
        }
      } catch ( error ) {
        console.error("Error fetching existing areas:", error);
        addToast("Error fetching existing areas", "error");
        setInspectionData(( prev ) => ({
          ...prev,
          originalAreas: []
        }));
      }
    } else {
      setExistingAreas([]);
      setInspectionData(( prev ) => ({
        ...prev,
        originalAreas: []
      }));
    }
  };

  useEffect(() => {
    fetchExistingAreas();
  }, [inspectionData.id, addToast]);



  const handleChangeInspectionData =
    ( e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> ) => {
      const { name, value } = e.target;

      setInspectionData(( prev ) => ({
        ...prev,
        [name]: value,
      }));
    }

  const handleMonthChange = useCallback(( monthIndex: number ) => {
    setSelectedMonthIndex(monthIndex);
    setInspectionData(( prev ) => ({
      ...prev,
      month: monthNames[monthIndex]
    }));
  }, [monthNames]);

  const handleExistingAreaChange = useCallback(
    ( id: number, field: keyof FacilityMonitoringAreaRowsProps, value: string ) => {
      setExistingAreas(( prev ) => prev.map(( item ) => (item.id === id ? { ...item, [field]: value } : item)));
    },
    []
  );

  const handleNewAreaChange = useCallback(
    <K extends keyof FacilityMonitoringAreaRowsProps>(
      index: number,
      field: K,
      value: FacilityMonitoringAreaRowsProps[K]
    ) => {
      setNewAreas(( prevRows ) => {
        const updated = [...prevRows];
        updated[index][field] = value;
        return updated;
      });
    },
    []
  );

  const handleAddNewAreaRow = useCallback(() => {
    setNewAreas(( prevRows ) => [...prevRows, { id: undefined, area: "", issue: "", revision: "" }]);
  }, []);

  const handleRemoveExistingArea = useCallback(
    async ( id: number ) => {
      // Instead of immediately deleting from the database, mark the area for deletion
      // when submitting the form
      setExistingAreas(( prev ) => prev.filter(( item ) => item.id !== id));
      addToast("Row marked for deletion. Save form to confirm.", "info");
    },
    [addToast]
  );

  const handleRemoveNewArea = useCallback(( index: number ) => {
    setNewAreas(( prev ) => prev.filter(( _, i ) => i !== index));
  }, []);

  const handleSubmitForm = useCallback(
    async ( e: React.FormEvent ) => {
      e.preventDefault();

      try {
        let facilityMonitoringId: number | undefined;

        if ( !inspectionData.id ) {
          // При создании новой записи также убираем originalAreas
          const { originalAreas, ...dataToCreate } = inspectionData;
          const created = await createFacilityMonitoring("facility_monitoring", {
            ...dataToCreate,
            wdid,
            user_id: user?.id,
            status: `Completed ${new Date().toLocaleDateString()}`,
            year: year,
            month: currentMonth,
          });

          if ( Array.isArray(created) && created.length > 0 && created[0].id ) {
            facilityMonitoringId = created[0].id;
            setInspectionData(( prev ) => ({
              ...prev,
              id: facilityMonitoringId
            }));
          } else if ( created?.data?.[0]?.id ) {
            facilityMonitoringId = created.data[0].id;
            setInspectionData(( prev ) => ({
              ...prev,
              id: facilityMonitoringId
            }));
          } else {
            throw new Error("Can't get id from facility_monitoring.");
          }
        } else {
          // При обновлении существующей записи также убираем originalAreas
          const { originalAreas, ...dataToUpdate } = inspectionData;
          await updateFacilityMonitoring(inspectionData.id, "facility_monitoring", {
            ...dataToUpdate,
            month: currentMonth,
          });
          facilityMonitoringId = inspectionData.id;
        }

        if ( !facilityMonitoringId ) {
          throw new Error("facilityMonitoringId is not defined.");
        }

        // Update existing areas
        if ( existingAreas.length > 0 ) {
          await Promise.all(
            existingAreas.map(async ( area ) => {
              // Remove originalAreas from area data before sending
              const { originalAreas, ...areaData } = area as any;
              const payload = {
                ...areaData,
                facility_monitoring_id: facilityMonitoringId,
              };
              return updateFacilityMonitoring(area.id, "facility_monitoring_area", payload);
            })
          );
        }

        // Process new areas
        const areasToProcess = newAreas.filter(( row ) => row.area || row.issue || row.revision);

        if ( areasToProcess.length > 0 ) {
          await Promise.all(
            areasToProcess.map(async ( row ) => {
              // Remove originalAreas from area data before sending
              const { originalAreas, ...rowData } = row as any;
              const payload = {
                ...rowData,
                facility_monitoring_id: facilityMonitoringId,
              };

              if ( row.id && row.id > 0 ) {
                // Update existing area
                return updateFacilityMonitoring(row.id, "facility_monitoring_area", payload);
              } else {
                // Create new area - remove id from payload
                const { id, ...payloadWithoutId } = payload;
                const created = await createFacilityMonitoring("facility_monitoring_area", payloadWithoutId);
                // Update ID in local state
                if ( created && Array.isArray(created) && created.length > 0 && created[0].id ) {
                  row.id = created[0].id;
                } else if ( created?.data?.[0]?.id ) {
                  row.id = created.data[0].id;
                }
                return created;
              }
            })
          );

          // After creating new areas, update originalAreas in local state
          // DO NOT send this to the database
          setInspectionData(( prev ) => ({
            ...prev,
            originalAreas: [
              ...existingAreas,
              ...areasToProcess.map(area => ({
                ...area,
                facility_monitoring_id: facilityMonitoringId
              }))
            ]
          }));
        }

        // Check if we need to delete areas that were removed from the form
        if ( inspectionData.originalAreas && inspectionData.originalAreas.length > 0 ) {
          const currentAreaIds = [...existingAreas, ...newAreas]
            .map(( area: FacilityMonitoringAreaProps | FacilityMonitoringAreaRowsProps ) => area.id)
            .filter(( id: number | undefined ): id is number => id !== undefined && id > 0);

          const originalAreaIds = inspectionData.originalAreas
            .map(( area: FacilityMonitoringAreaProps | FacilityMonitoringAreaRowsProps ) => area.id)
            .filter(( id: number | undefined ): id is number => id !== undefined && id > 0);

          const areasToDelete = originalAreaIds.filter(( id: number ) => !currentAreaIds.includes(id));

          if ( areasToDelete.length > 0 ) {
            await Promise.all(
              areasToDelete.map(( id: number ) =>
                deleteFacilityMonitoring(id, "facility_monitoring_area")
              )
            );
          }
        }

        addToast("Successfully updated!", "success");
        setTimeout(() => {
          window.location.href = "/monitoring";
        }, 2000);
      } catch ( error: any ) {
        console.error("Error updating data:", error);
        addToast("Error updating data", "error");
      }
    },
    [inspectionData, existingAreas, newAreas, wdid, user?.id, year, currentMonth, addToast]
  );

  const handleDeleteClick = useCallback(() => {
    if ( !inspectionData.id ) {
      addToast("No 'id' found. Could not delete record!", "error");
      return;
    }
    setIsOpen(true);
  }, [inspectionData.id, addToast]);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleConfirmDelete = useCallback(
    async ( confirmed: boolean ) => {
      setIsOpen(false);
      if ( confirmed && inspectionData.id ) {
        try {
          await deleteFacilityMonitoring(inspectionData.id, "facility_monitoring_area?facility_monitoring_id");
          await deleteFacilityMonitoring(inspectionData.id, "facility_monitoring");
          addToast("Form deleted successfully!", "success");
          setTimeout(() => {
            window.location.href = "/monitoring";
          }, 2000);
        } catch ( error ) {
          console.error("Error deleting data:", error);
          addToast("Error deleting form!", "error");
        }
      }
    },
    [inspectionData.id, addToast]
  );

  const handleCancelDelete = useCallback(() => {
    setIsOpen(false);
  }, []);

  const reactToPrintContentCallback = useCallback(() => componentRef.current, []);
  return (
    <div
      style={{
        display: "flex",
        width: "80%",
        margin: "40px auto",
        flexDirection: "column",
        gap: "10px",
      }}
    >
      <ModalWarning isOpen={isOpen} onConfirm={handleConfirmDelete} onCancel={handleCancelDelete}/>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button color="white" type="button" background="darkBlue">
          <a href="/monitoring" style={{ color: "inherit", textDecoration: "none" }}>
            Go back
          </a>
        </Button>
      </div>

      <form
        onSubmit={handleSubmitForm}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "baseline",
          flexWrap: "wrap",
        }}
      >
        <div style={{ justifyContent: "flex-end", width: "100%", display: "flex" }}>
          {isReadOnly ? (
            <ReactToPrint
              content={reactToPrintContentCallback}
              documentTitle="Monitoring Form"
              removeAfterPrint
              trigger={() => (
                <Button type="button" color="white" background="blue">
                  Print completed form
                </Button>
              )}
            />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {!inspectionData.url && (
                <Button type="submit" color="white" background="blue">
                  Save Form
                </Button>
              )}
              <Button color="white" type="button" onClick={handleDeleteClick}>
                Delete Form
              </Button>
            </div>
          )}
        </div>

        <h1 style={{ margin: '20px auto 20px', textAlign: 'center' }}>
          Monthly Visual Observation Form
          {year ? ` - ${year}` : ""}
        </h1>

        <div style={{ paddingLeft: '16px' }}>
          <ol>
            <li>Complete during daylight operating hours on days without precipitation.</li>
            <li>Section IV.B.3 of the Industrial General Permit states:
              <ul>
                <li>
                  Reduce or prevent the contact of authorized NSWDs with materials or equipment that are potential
                  sources of pollutants;
                </li>
                <li>
                  Reduce, to the extent practicable, the flow or volume of authorized NSWDs;
                </li>
                <li>
                  Ensure that authorized NSWDs do not contain quantities of pollutants that cause or contribute to an
                  exceedance of a water quality standards;
                </li>
                <li>
                  Reduce or prevent discharges of pollutants in authorized NSWDs in a manner that reflects best industry
                  practice considering technological availability and economic
                  practicability and achievability.
                </li>
              </ul>
            </li>
            <li>Visually observe outdoor industrial equipment and storage areas, outdoor industrial activity areas and
              associated BMPs identified in the SWPPP. Facilty shall revise BMPs as necessary when the visual
              observations indicate pollutant sources have not been adequately addressed in the SWPPP.
            </li>
          </ol>
        </div>

        <div
          ref={componentRef}
          id="printable-section"
          style={{
            width: !isReadOnly ? "100%" : isReadOnly && !inspectionData.url ? "100%" : "auto",
            margin: "20px auto",
            pointerEvents: isReadOnly ? "none" : "auto",
            opacity: isReadOnly ? 0.9 : 1,
          }}
        >
          {inspectionData.url ? (
            <img src={inspectionData.url} alt="Monthly-Facility-Form" style={{ width: "100%", height: "auto" }}/>
          ) : (
            <FormTable>
              <FormHeader>
                <FormCell style={{ display: "flex", justifyContent: "center" }}>General Information</FormCell>
              </FormHeader>

              <FormRow
                style={{
                  width: "100%",
                  padding: "5px",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                {isReadOnly ? (
                  <ReadOnlyField label="Month" value={currentMonth}/>
                ) : (
                  monthNames.map(( item, idx ) => (
                    <p
                      key={item}
                      style={
                        idx === selectedMonthIndex
                          ? {
                            fontWeight: "bold",
                            textDecoration: "underline",
                            cursor: "default",
                          }
                          : { cursor: "pointer" }
                      }
                      onClick={() => handleMonthChange(idx)}
                    >
                      {item}
                    </p>
                  ))
                )}
              </FormRow>

              <FormRow className="row">
                <FormCell style={{ display: "flex", gap: 5 }}>
                  <FormLabel>Facility:</FormLabel>
                  {isReadOnly ? <span>{inspectionData.wdid}</span> : wdid}
                </FormCell>
                <FormCell style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <FormLabel>Date and Time</FormLabel>
                  {isReadOnly ? (
                    <span>{inspectionData.date_time?.split("T")[0]}</span>
                  ) : (
                    <FormInput
                      type="date"
                      name="date_time"
                      value={inspectionData.date_time?.split("T")[0] || ""}
                      onChange={handleChangeInspectionData}
                      required
                    />
                  )}
                </FormCell>
                <FormCell style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <FormLabel>Observer Name and Title</FormLabel>
                  {isReadOnly ? (
                    <span>{inspectionData.title}</span>
                  ) : (
                    <FormInput
                      type="text"
                      name="title"
                      value={inspectionData.title}
                      onChange={handleChangeInspectionData}
                      required
                    />
                  )}
                </FormCell>
              </FormRow>

              <FormRow>
                <FormCell style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <FormLabel>Preceding Weather Conditions (Previous 48 hours)</FormLabel>
                  {isReadOnly ? (
                    <span>{inspectionData.preceding_weather_condition}</span>
                  ) : (
                    <FormInput
                      type="text"
                      name="preceding_weather_condition"
                      value={inspectionData.preceding_weather_condition || ""}
                      onChange={handleChangeInspectionData}
                      required
                    />
                  )}
                </FormCell>
                <FormCell style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <FormLabel>Current Weather Conditions</FormLabel>
                  {isReadOnly ? (
                    <span>{inspectionData.current_weather_condition}</span>
                  ) : (
                    <FormInput
                      type="text"
                      name="current_weather_condition"
                      value={inspectionData.current_weather_condition || ""}
                      onChange={handleChangeInspectionData}
                      required
                    />
                  )}
                </FormCell>
              </FormRow>

              <br/>
              <br/>

              <FormHeader>
                <FormCell style={{ display: "flex", justifyContent: "center" }}>
                  Non-Storm Water Discharge (NSWD) Observations
                </FormCell>
              </FormHeader>

              <FormRow>
                <FormCell style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <FormLabel>Was a NSWD observed?</FormLabel>
                  {isReadOnly ? (
                    <span>{inspectionData.nswd ? "Yes" : "No"}</span>
                  ) : (
                    <RadioGroup
                      name="nswd"
                      value={inspectionData.nswd}
                      onChange={( val ) => setInspectionData(( prev ) => ({ ...prev, nswd: val }))}
                      required
                    />
                  )}
                </FormCell>
                <FormCell style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <FormLabel style={{ opacity: inspectionData.nswd ? '0.2' : '1' }}>What was the source?</FormLabel>
                  {isReadOnly || inspectionData.nswd ? (
                    <span>{inspectionData.nswd_source}</span>
                  ) : (
                    <FormInput
                      type="text"
                      name="nswd_source"
                      value={inspectionData.nswd_source || ""}
                      onChange={handleChangeInspectionData}
                      required
                    />
                  )}
                </FormCell>
                <FormCell style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <FormLabel>Was it Authorized or UnAuthorized NSWD?</FormLabel>
                  {isReadOnly ? (
                    <span>{inspectionData.nswd_authorized ? "Authorized" : "Unauthorized"}</span>
                  ) : (
                    <RadioGroup
                      name="nswd_authorized"
                      value={inspectionData.nswd_authorized}
                      onChange={( val ) => setInspectionData(( prev ) => ({ ...prev, nswd_authorized: val }))}
                      required
                    />
                  )}
                </FormCell>
              </FormRow>

              <FormRow>
                <FormCell/>
                <FormCell
                  style={
                    inspectionData.nswd
                      ? { display: "flex", flexDirection: "column", gap: 5 }
                      : { display: "flex", opacity: 0.2, flexDirection: "column", gap: 5 }
                  }
                >
                  <FormLabel>
                    Did you observe trash, debris, suspended material, oil sheen, discoloration, odors?
                  </FormLabel>
                  {isReadOnly && inspectionData.nswd ? (
                    <span>{inspectionData.nswd_observe ? "Yes" : "No"}</span>
                  ) : (
                    inspectionData.nswd && (
                      <RadioGroup
                        name="nswd_observe"
                        value={inspectionData.nswd_observe}
                        onChange={( val ) => setInspectionData(( prev ) => ({ ...prev, nswd_observe: val }))}
                        required
                      />
                    )
                  )}
                </FormCell>
                <FormCell
                  style={
                    inspectionData.nswd_authorized
                      ? { display: "flex", flexDirection: "column", gap: 5 }
                      : { display: "flex", opacity: 0.2, flexDirection: "column", gap: 5 }
                  }
                >
                  <FormLabel>Does the Authorized NSWD and associated BMPs comply with Section IV.B.3</FormLabel>
                  {isReadOnly && inspectionData.nswd_authorized ? (
                    <span>{inspectionData.nswd_bmp ? "Yes" : "No"}</span>
                  ) : (
                    inspectionData.nswd_authorized && (
                      <RadioGroup
                        name="nswd_bmp"
                        value={inspectionData.nswd_bmp}
                        onChange={( val ) => setInspectionData(( prev ) => ({ ...prev, nswd_bmp: val }))}
                        required
                      />
                    )
                  )}
                </FormCell>
              </FormRow>

              <FormRow>
                <FormCell/>
                <FormCell
                  style={
                    inspectionData.nswd_observe
                      ? { display: "flex", flexDirection: "column", gap: 5 }
                      : { display: "flex", opacity: 0.2, flexDirection: "column", gap: 5 }
                  }
                >
                  <FormLabel>Describe visual observation</FormLabel>
                  {isReadOnly && inspectionData.nswd_observe ? (
                    <span>{inspectionData.nswd_above}</span>
                  ) : (
                    inspectionData.nswd_observe && (
                      <FormInput
                        type="text"
                        name="nswd_above"
                        value={inspectionData.nswd_above || ""}
                        onChange={handleChangeInspectionData}
                        required
                      />
                    )
                  )}
                </FormCell>
                <FormCell
                  style={
                    !inspectionData.nswd_authorized
                      ? { display: "flex", flexDirection: "column", gap: 5 }
                      : { display: "flex", opacity: 0.2, flexDirection: "column", gap: 5 }
                  }
                >
                  <FormLabel>What was the action to eliminate the discharge?</FormLabel>
                  {isReadOnly && !inspectionData.nswd_authorized ? (
                    <span>{inspectionData.action_to_eliniminate}</span>
                  ) : (
                    !inspectionData.nswd_authorized && (
                      <FormInput
                        type="text"
                        name="action_to_eliniminate"
                        value={inspectionData.action_to_eliniminate || ""}
                        onChange={handleChangeInspectionData}
                        required
                      />
                    )
                  )}
                </FormCell>
              </FormRow>

              <FormRow>
                <FormCell style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <FormLabel>Was indication of a prior or potential NSWD observed?</FormLabel>
                  {isReadOnly ? (
                    <span>{inspectionData.nswd_potential_observed ? "Yes" : "No"}</span>
                  ) : (
                    <RadioGroup
                      name="nswd_potential_observed"
                      value={inspectionData.nswd_potential_observed}
                      onChange={( val ) => setInspectionData(( prev ) => ({ ...prev, nswd_potential_observed: val }))}
                      required
                    />
                  )}
                </FormCell>
                <FormCell
                  style={
                    inspectionData.nswd_potential_observed
                      ? { display: "flex", flexDirection: "column", gap: 5 }
                      : { display: "flex", opacity: 0.2, flexDirection: "column", gap: 5 }
                  }
                >
                  <FormLabel>What was the source and describe visual evidence observed?</FormLabel>
                  {isReadOnly ? (
                    <span>{inspectionData.nswd_visual_observed}</span>
                  ) : (
                    inspectionData.nswd_potential_observed && (
                      <FormInput
                        type="text"
                        name="nswd_visual_observed"
                        value={inspectionData.nswd_visual_observed || ""}
                        onChange={handleChangeInspectionData}
                        required
                      />
                    )
                  )}
                </FormCell>
                <FormCell
                  style={
                    !inspectionData.nswd_authorized
                      ? { display: "flex", flexDirection: "column", gap: 5 }
                      : { display: "flex", opacity: 0.2, flexDirection: "column", gap: 5 }
                  }
                >
                  <FormLabel>What was the action taken?</FormLabel>
                  {isReadOnly && !inspectionData.nswd_authorized ? (
                    <span>{inspectionData.nswd_action}</span>
                  ) : (
                    !inspectionData.nswd_authorized && (
                      <FormInput
                        type="text"
                        name="nswd_action"
                        value={inspectionData.nswd_action || ""}
                        onChange={handleChangeInspectionData}
                        required
                      />
                    )
                  )}
                </FormCell>
              </FormRow>

              <br/>
              <br/>

              <FormHeader>
                <FormCell style={{ display: "flex", justifyContent: "center" }}>
                  Observations of Outdoor Industrial
                  Activity Areas, Storage Areas, BMPs, and ALL Other Potential
                  Sources of Pollutants
                </FormCell>
              </FormHeader>

              <FormRow style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <OOIAASComponent
                  value={inspectionData.ooiaas_swppp}
                  isReadOnly={isReadOnly}
                  onChange={( val ) => setInspectionData(( prev ) => ({ ...prev, ooiaas_swppp: val }))}
                  label="Are BMPs in place and implemented per the frequency identified in the SWPPP?"
                  aditionalText="If no, complete section below"
                  radioGroupName="ooiaas_swppp"
                />
                <OOIAASComponent
                  value={inspectionData.ooiaas_bmp_good}
                  isReadOnly={isReadOnly}
                  onChange={( val ) => setInspectionData(( prev ) => ({ ...prev, ooiaas_bmp_good: val }))}
                  label="Is the equipment needed to implement BMPs in good condition?"
                  aditionalText="If no, complete section below"
                  radioGroupName="ooiaas_bmp_good"
                />
                <OOIAASComponent
                  value={inspectionData.ooiaas_bmp_maintenance}
                  isReadOnly={isReadOnly}
                  onChange={( val ) => setInspectionData(( prev ) => ({ ...prev, ooiaas_bmp_maintenance: val }))}
                  label="Do BMPs need maintenance?"
                  aditionalText="If yes, complete section below"
                  radioGroupName="ooiaas_bmp_maintenance"
                />
                <OOIAASComponent
                  value={inspectionData.ooiaas_swppp_revision}
                  isReadOnly={isReadOnly}
                  onChange={( val ) => setInspectionData(( prev ) => ({ ...prev, ooiaas_swppp_revision: val }))}
                  label="Are there any changes to the industrial activities or potential pollutant sources that require a SWPPP revision?"
                  aditionalText="If yes, complete section below"
                  radioGroupName="ooiaas_swppp_revision"
                />
              </FormRow>

              <br/>
              <br/>

              <FormHeader style={{ display: "flex", justifyContent: "center" }}>
                <FormCell>
                  <>Area</>
                </FormCell>
                <FormCell>
                  <>Issue Identified</>
                </FormCell>
                <FormCell>
                  <>Corrective Action / SWPPP Revisions</>
                </FormCell>
                {!isReadOnly && <FormCell style={{ maxWidth: "100px" }}/>}
              </FormHeader>

              {existingAreas.map(( area ) => (
                <ExistingAreaRow
                  key={area.id}
                  area={area}
                  isReadOnly={isReadOnly}
                  onChange={handleExistingAreaChange}
                  onDelete={handleRemoveExistingArea}
                />
              ))}

              {!isReadOnly &&
                newAreas.map(( row, index ) => (
                  <NewAreaRow
                    key={`new-area-${index}`}
                    row={row}
                    index={index}
                    isReadOnly={isReadOnly}
                    onChange={handleNewAreaChange}
                    onDelete={handleRemoveNewArea}
                    required={inspectionData.ooiaas_swppp || inspectionData.ooiaas_bmp_good || !inspectionData.ooiaas_bmp_maintenance || !inspectionData.ooiaas_swppp_revision}
                  />
                ))}

              {!isReadOnly && (
                <FormRow style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button type="button" color="white" background="grey" onClick={handleAddNewAreaRow}>
                    Add New Row
                  </Button>
                </FormRow>
              )}
            </FormTable>
          )}
        </div>
      </form>
    </div>
  );
};

interface OOIAASComponentProps {
  label: string;
  isReadOnly: boolean;
  value: boolean | undefined;
  onChange: ( value: boolean ) => void;
  aditionalText: string;
  radioGroupName: string;
}

const OOIAASComponent: React.FC<OOIAASComponentProps> = ( {
                                                            label,
                                                            isReadOnly,
                                                            value,
                                                            onChange,
                                                            aditionalText,
                                                            radioGroupName
                                                          } ) => (
  <FormCell style={{
    display: "flex",
    flexDirection: "row",
    gap: 25,
    justifyContent: "space-between"
  }}>
    <FormLabel>{label}</FormLabel>
    <div style={{ display: "flex", flexDirection: "row", gap: 10 }}>
      {isReadOnly ? (
        <span>{value ? "Yes" : "No"}</span>
      ) : (
        <RadioGroup
          name={radioGroupName}
          value={value}
          onChange={onChange}
          required
        />
      )}
      |
      <span>{aditionalText}</span>
    </div>
  </FormCell>
);

export default StormWaterInspectionForm;
